/**
 * Phase MGR1 — Manager rental detail page (mobile-first).
 *
 * Single column. Sections: Customer (with tap-to-call phone), Equipment,
 * Rental dates, Inspection form (native camera capture), Record return CTA.
 * Once return is recorded, the page becomes a read-only review.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError, getBackendBase } from "@/lib/api";
import { extractDigits, formatPhoneForDisplay } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Phone, Camera, Trash2, CheckCircle2, AlertTriangle, ClipboardCheck, LogOut, Plus } from "lucide-react";

const CONDITIONS = [
    { value: "good", label: "Good — no issues", tone: "emerald" },
    { value: "fair", label: "Fair — minor wear", tone: "amber" },
    { value: "damaged", label: "Damaged — needs repair", tone: "red" },
];

function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function formatDateTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function Section({ title, children, testid }) {
    return (
        <section
            data-testid={testid}
            className="rounded-xl border border-slate-200 bg-white p-4"
        >
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                {title}
            </h3>
            {children}
        </section>
    );
}

function StatusBanner({ rental }) {
    if (rental?.return_recorded_at) {
        return (
            <div
                data-testid="manager-status-banner-recorded"
                className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3"
            >
                <CheckCircle2 size={20} className="text-amber-900 mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="font-semibold text-amber-900">Return recorded</div>
                    <div className="text-sm text-amber-800 mt-0.5">
                        {formatDateTime(rental.return_recorded_at)}
                        {rental.return_recorded_by_name ? ` · by ${rental.return_recorded_by_name}` : ""}
                    </div>
                    <div className="text-xs text-amber-800 mt-1">
                        Waiting on admin to complete final paperwork.
                    </div>
                </div>
            </div>
        );
    }
    if (rental?.inspection_submitted) {
        return (
            <div
                data-testid="manager-status-banner-ready"
                className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3"
            >
                <ClipboardCheck size={20} className="text-emerald-900 mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="font-semibold text-emerald-900">Inspection submitted</div>
                    <div className="text-sm text-emerald-800 mt-0.5">
                        Ready to record the return when the trailer is on the lot.
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div
            data-testid="manager-status-banner-pending"
            className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3"
        >
            <AlertTriangle size={20} className="text-blue-900 mt-0.5 shrink-0" />
            <div className="flex-1">
                <div className="font-semibold text-blue-900">Inspection required</div>
                <div className="text-sm text-blue-800 mt-0.5">
                    Walk the equipment, capture photos, then submit below.
                </div>
            </div>
        </div>
    );
}

function PhotoTile({ photo, onDelete, canDelete }) {
    return (
        <div
            className="relative aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-100"
            data-testid={`manager-photo-tile-${photo.id}`}
        >
            <img
                src={`${getBackendBase()}/api/admin/rentals/_/inspection/photos/${photo.id}/raw`}
                alt={photo.filename || "photo"}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute bottom-1 left-1 right-1 text-[10px] font-mono bg-black/60 text-white px-1 rounded truncate">
                {photo.filename || photo.id?.slice(0, 6)}
            </div>
            {canDelete && (
                <button
                    type="button"
                    onClick={() => onDelete(photo.id)}
                    data-testid={`manager-photo-delete-${photo.id}`}
                    className="absolute top-1 right-1 h-7 w-7 rounded-full bg-red-600 text-white flex items-center justify-center shadow"
                    aria-label="Delete photo"
                >
                    <Trash2 size={14} />
                </button>
            )}
        </div>
    );
}

export default function ManagerRentalDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { tenant } = useTenant();

    const [rental, setRental] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Inspection form state (mirrors DP-R2 schema)
    const [condition, setCondition] = useState("good");
    const [notes, setNotes] = useState("");
    const [damages, setDamages] = useState([]);
    const [saving, setSaving] = useState(false);
    const [confirmReturn, setConfirmReturn] = useState(false);
    const photoInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/manager/rentals/${id}`);
            setRental(data);
            if (data?.inspection) {
                setCondition(data.inspection.condition || "good");
                setNotes(data.inspection.notes || "");
                setDamages(data.inspection.damages || []);
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const inspectionLocked = !!rental?.return_recorded_at;
    const inspectionSubmitted = !!rental?.inspection_submitted;
    const canRecordReturn = inspectionSubmitted && !inspectionLocked;

    const addDamage = () => setDamages((d) => [...d, { area: "", severity: "minor", description: "" }]);
    const updateDamage = (idx, patch) =>
        setDamages((d) => d.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    const removeDamage = (idx) => setDamages((d) => d.filter((_, i) => i !== idx));

    const submitInspection = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/rentals/${id}/inspection`, {
                condition,
                notes,
                damages,
            });
            toast.success("Inspection saved.");
            await load();
        } catch (err) {
            // Phase PWA — only queue genuine network / 5xx failures.
            // 4xx is validation and should surface immediately.
            const status = err?.response?.status;
            const isNetworkOrServer = !status || status >= 500;
            if (isNetworkOrServer) {
                try {
                    const { enqueueInspection } = await import("@/pwa/inspection-queue");
                    await enqueueInspection({
                        rental_id: id,
                        metadata: { condition, notes, damages },
                        photos: [],
                    });
                    toast.success(
                        "Saved offline — will sync automatically when you're back online.",
                        { duration: 6000 },
                    );
                } catch (queueErr) {
                    toast.error(formatApiError(err));
                }
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setSaving(false);
        }
    };

    const onPhotoPick = async (ev) => {
        const files = Array.from(ev.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        try {
            const fd = new FormData();
            for (const f of files) fd.append("photos", f);
            await api.post(`/admin/rentals/${id}/inspection/photos`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} uploaded.`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const onDeletePhoto = async (photoId) => {
        try {
            await api.delete(`/admin/rentals/${id}/inspection/photos/${photoId}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onRecordReturn = async () => {
        setConfirmReturn(false);
        try {
            await api.post(`/manager/rentals/${id}/record-return`);
            toast.success("Return recorded. Admin notified.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const photos = useMemo(() => rental?.inspection?.photos || [], [rental]);

    const displayName = tenant?.display_name || "RentFlow";
    const logoUrl = tenant?.logo_url || "/favicon.png";

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
                <div className="max-w-xl mx-auto px-4 h-14 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => navigate("/manager")}
                        className="text-slate-700 hover:text-blue-900 active:text-blue-900 p-2 -ml-2"
                        aria-label="Back"
                        data-testid="manager-back"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <img
                        src={logoUrl}
                        alt={displayName}
                        className="h-7 w-auto max-w-[80px] object-contain rounded"
                    />
                    <div className="leading-tight flex-1 min-w-0">
                        <div className="font-display font-bold text-sm tracking-tight text-slate-900 truncate">
                            {displayName}
                        </div>
                        <div className="text-[9px] uppercase tracking-[0.18em] text-blue-900">
                            Yard · rental
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => { logout(); navigate("/login", { replace: true }); }}
                        className="text-slate-500 hover:text-slate-900 p-2 -mr-2"
                        aria-label="Sign out"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </header>

            <main className="flex-1 max-w-xl w-full mx-auto px-4 py-4 space-y-4 pb-32">
                {loading ? (
                    <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
                ) : error ? (
                    <div
                        data-testid="manager-load-error"
                        className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-900"
                    >
                        {error}
                    </div>
                ) : (
                    <>
                        <StatusBanner rental={rental} />

                        <Section title="Customer" testid="manager-section-customer">
                            <div className="font-semibold text-slate-900 text-lg">
                                {rental.customer?.display_name || "—"}
                            </div>
                            {rental.customer?.main_contact_phone ? (
                                <a
                                    data-testid="manager-customer-phone"
                                    href={`tel:${extractDigits(rental.customer.main_contact_phone)}`}
                                    className="mt-1 inline-flex items-center gap-2 text-blue-900 underline-offset-2 hover:underline"
                                >
                                    <Phone size={14} />
                                    {formatPhoneForDisplay(rental.customer.main_contact_phone) || rental.customer.main_contact_phone}
                                </a>
                            ) : (
                                <div className="text-sm text-slate-400 mt-1">No phone on file</div>
                            )}
                        </Section>

                        <Section title="Equipment" testid="manager-section-equipment">
                            <div className="text-slate-900 text-lg font-mono font-semibold tracking-tight">
                                {rental.equipment?.unit_number || "—"}
                            </div>
                            <div className="text-sm text-slate-600 mt-0.5">
                                {[rental.equipment?.year, rental.equipment?.make, rental.equipment?.model].filter(Boolean).join(" ") || "—"}
                            </div>
                            {rental.equipment?.serial && (
                                <div className="text-xs text-slate-500 mt-1 font-mono">
                                    Serial · {rental.equipment.serial}
                                </div>
                            )}
                        </Section>

                        <Section title="Rental dates" testid="manager-section-dates">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Start</div>
                                    <div className="text-slate-900">{formatDate(rental.start_date)}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400">End</div>
                                    <div className="text-slate-900">{formatDate(rental.end_date)}</div>
                                </div>
                            </div>
                        </Section>

                        <Section title="Inspection" testid="manager-section-inspection">
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-slate-500">Condition</Label>
                                    {CONDITIONS.map((c) => {
                                        const selected = condition === c.value;
                                        return (
                                            <button
                                                key={c.value}
                                                type="button"
                                                disabled={inspectionLocked}
                                                onClick={() => setCondition(c.value)}
                                                data-testid={`manager-condition-${c.value}`}
                                                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                                                    selected
                                                        ? `border-${c.tone}-500 bg-${c.tone}-50 text-${c.tone}-900 font-semibold`
                                                        : "border-slate-200 bg-white text-slate-700"
                                                } ${inspectionLocked ? "opacity-60" : "active:scale-[0.99]"}`}
                                            >
                                                {c.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div>
                                    <Label htmlFor="mgr-notes" className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
                                    <Textarea
                                        id="mgr-notes"
                                        data-testid="manager-inspection-notes"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        disabled={inspectionLocked}
                                        placeholder="Optional. Mention anything not covered by the photos."
                                        rows={3}
                                        className="mt-1"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-slate-500">Damages</Label>
                                    {damages.length === 0 && (
                                        <div className="text-sm text-slate-500">No damages recorded.</div>
                                    )}
                                    {damages.map((d, idx) => (
                                        <div
                                            key={idx}
                                            data-testid={`manager-damage-row-${idx}`}
                                            className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50"
                                        >
                                            <div className="flex justify-between items-center gap-2">
                                                <div className="text-xs font-semibold text-slate-500">
                                                    Damage #{idx + 1}
                                                </div>
                                                {!inspectionLocked && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDamage(idx)}
                                                        className="h-8 w-8 rounded-full bg-red-50 text-red-700 active:bg-red-100 flex items-center justify-center"
                                                        aria-label="Remove"
                                                        data-testid={`manager-damage-remove-${idx}`}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <Input
                                                value={d.area || ""}
                                                onChange={(e) => updateDamage(idx, { area: e.target.value })}
                                                disabled={inspectionLocked}
                                                placeholder="Area (e.g. left rear corner)"
                                                data-testid={`manager-damage-area-${idx}`}
                                            />
                                            <Input
                                                value={d.description || ""}
                                                onChange={(e) => updateDamage(idx, { description: e.target.value })}
                                                disabled={inspectionLocked}
                                                placeholder="Description"
                                                data-testid={`manager-damage-description-${idx}`}
                                            />
                                        </div>
                                    ))}
                                    {!inspectionLocked && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={addDamage}
                                            data-testid="manager-add-damage"
                                            className="w-full gap-2"
                                        >
                                            <Plus size={14} />
                                            Add damage
                                        </Button>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-slate-500">Photos</Label>
                                    {photos.length > 0 ? (
                                        <div className="grid grid-cols-3 gap-2">
                                            {photos.map((p) => (
                                                <PhotoTile
                                                    key={p.id}
                                                    photo={p}
                                                    onDelete={onDeletePhoto}
                                                    canDelete={
                                                        !inspectionLocked
                                                        && p.uploaded_by === user?.id
                                                    }
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-slate-500">No photos yet.</div>
                                    )}
                                    {!inspectionLocked && (
                                        <>
                                            <input
                                                ref={photoInputRef}
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                multiple
                                                onChange={onPhotoPick}
                                                className="hidden"
                                                data-testid="manager-inspection-photo-input"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                disabled={uploading}
                                                onClick={() => photoInputRef.current?.click()}
                                                data-testid="manager-photo-capture"
                                                className="w-full gap-2 h-12"
                                            >
                                                <Camera size={16} />
                                                {uploading ? "Uploading…" : "Capture photo"}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </Section>

                        {/* Pinned bottom action bar */}
                        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 z-30">
                            <div className="max-w-xl mx-auto flex gap-2">
                                {!inspectionLocked && (
                                    <Button
                                        type="button"
                                        onClick={submitInspection}
                                        disabled={saving}
                                        data-testid="manager-inspection-submit"
                                        className="flex-1 h-14 text-base font-semibold bg-blue-900 hover:bg-blue-800"
                                    >
                                        {saving ? "Saving…" : (inspectionSubmitted ? "Update inspection" : "Submit inspection")}
                                    </Button>
                                )}
                                {canRecordReturn && (
                                    <Button
                                        type="button"
                                        onClick={() => setConfirmReturn(true)}
                                        data-testid="manager-record-return"
                                        className="flex-1 h-14 text-base font-semibold bg-emerald-700 hover:bg-emerald-800 gap-2"
                                    >
                                        <CheckCircle2 size={18} />
                                        Record return
                                    </Button>
                                )}
                                {inspectionLocked && (
                                    <div
                                        data-testid="manager-locked-state"
                                        className="flex-1 h-14 flex items-center justify-center text-emerald-900 bg-emerald-50 rounded-md text-sm font-semibold gap-2"
                                    >
                                        <CheckCircle2 size={16} />
                                        Return recorded
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </main>

            <Dialog open={confirmReturn} onOpenChange={setConfirmReturn}>
                <DialogContent
                    className="max-w-sm"
                    data-testid="manager-confirm-return-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display">Record this return?</DialogTitle>
                        <DialogDescription>
                            This notifies the customer and your admin team that the equipment
                            is back at the yard. The admin will finish closing out the rental
                            (final invoice + deposit).
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setConfirmReturn(false)}
                            data-testid="manager-confirm-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={onRecordReturn}
                            data-testid="manager-confirm-submit"
                            className="bg-emerald-700 hover:bg-emerald-800"
                        >
                            Yes, record return
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
